import { Star } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { useStarLookup, useToggleStar } from "~/api/queries";
import { Tooltip } from "~/foundation/components/Tooltip";
import { cn } from "~/foundation/utils/cn";

/**
 * W10 §2 — the one star affordance, for a document or a collection.
 *
 * A star is the viewer's own bookmark, so nothing here is gated on an ability: the server checks
 * only the session (`StarsController` never consults the document's policy), and a reader who
 * may not edit a document is exactly the person who wants to keep a link to it.
 *
 * A plain button rather than `IconButton`, because the STATE is the icon's weight — filled or
 * outline — and a shared control that only knows `active` would have to say "starred" in words
 * as well. Metrics match `IconButton`'s `md` so a header row of both still lines up.
 */
export function StarButton({
  documentId,
  collectionId,
}: {
  documentId?: string;
  collectionId?: string;
}) {
  const { t } = useTranslation();
  const entityId = documentId ?? collectionId ?? "";
  const starIdFor = useStarLookup();
  const toggle = useToggleStar();
  const starred = starIdFor(entityId) !== undefined;
  const label = starred ? t("Unstar") : t("Star");

  if (!entityId) {
    return null;
  }

  return (
    <Tooltip content={label}>
      <button
        type="button"
        aria-label={label}
        aria-pressed={starred}
        disabled={toggle.isPending}
        onClick={() =>
          toggle.mutate(documentId ? { documentId } : { collectionId: collectionId ?? "" })
        }
        className={cn(
          "inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-sm",
          "transition-colors duration-100 ease-in-out motion-reduce:transition-none",
          "hover:bg-hover-bg disabled:pointer-events-none disabled:opacity-50",
          starred ? "text-accent" : "text-text-secondary hover:text-text",
        )}
      >
        <Star size={18} weight={starred ? "fill" : "regular"} aria-hidden />
      </button>
    </Tooltip>
  );
}
